import * as Accordion from '@radix-ui/react-accordion'
import { useEffect } from 'react'

import { Button } from '@/components/button/button'
import { Spinner } from '@/components/spinner/spinner'
import { fontStyles } from '@/components/typography/typographyUtils'
import { useOldBookingCompletion } from '@/features/booking'
import { LocationViewCounter } from '@/features/location'
import { mapStoreToNewReservation, useReserveOrder } from '@/features/reservation'
import { useRoomSelectionQuery } from '@/features/url-handling'
import { cn } from '@/libs/tw'
import { useCartStore } from '@/stores/cart-store/cartStore'
import { useStepStore } from '@/stores/step-store/stepStore'
import { translate } from '@/utils/translator'

import { RoomContextProvider } from '../context/roomContext'
import { RoomStateSwitcher } from './roomStateSwitcher'

export const RoomSelectionContent = () => {
	useRoomSelectionQuery()
	const setNextStep = useStepStore.use.setNextStep()
	const roomConfigurations = useCartStore.use.roomConfigurations()
	const { selectedRooms } = useCartStore.use.roomSelection()
	const location = useCartStore.use.location()
	const setReservationId = useCartStore.use.setReservationId()
	const { handleOldBookingCompletion } = useOldBookingCompletion()

	const { mutate: reserveOrder, isPending } = useReserveOrder({
		onSuccess: (data) => {
			if (data.reservationStatus === 'Initiate') {
				setReservationId(data.reservationId)
				setNextStep()
			}
		},
	})

	const isStepDone = roomConfigurations.length === selectedRooms.length

	const handleReserveOrder = () => {
		const cart = useCartStore.getState()
		const useOldCheckout = Boolean(cart.location?.useLegacyCheckout)

		if (useOldCheckout) {
			handleOldBookingCompletion()
			return
		}

		const reservationBody = mapStoreToNewReservation(cart)

		if (!reservationBody) return

		reserveOrder(reservationBody)
	}

	const searchParams = new URLSearchParams(window.location.search)
	const showTouristTax =
		searchParams.has('utm_source') &&
		searchParams.get('utm_source')?.toLocaleLowerCase() === 'googlehpa'

	useEffect(() => {
		if (isStepDone) {
			setTimeout(() => {
				const element = document.getElementById('BookingCompletionButton')
				if (!element) return

				const fixedHeaderHeight = document.querySelector('header')?.offsetHeight ?? 0
				const elementBottomPosition =
					element.getBoundingClientRect().bottom + window.scrollY
				const viewportHeight = window.innerHeight

				const scrollPosition = elementBottomPosition - viewportHeight + fixedHeaderHeight

				window.scroll({
					top: scrollPosition,
					behavior: 'smooth',
				})
			}, 50)
		}
	}, [isStepDone])

	return (
		<div className={cn('flex flex-col gap-48')}>
			{location && (
				<div
					dangerouslySetInnerHTML={{ __html: location.description }}
					className={cn(fontStyles['p'], 'roomDescription text-textColor md:w-2/3')}
				/>
			)}

			<LocationViewCounter />
			<div className="flex flex-col gap-4">
				{roomConfigurations.length > 1 ? (
					<Accordion.Root
						type={'multiple'}
						defaultValue={roomConfigurations.map((rc) => rc.id)}
					>
						{roomConfigurations.map((roomConfig, index) => {
							const isRoomSelected = selectedRooms.find(
								(sr) => sr.roomConfigId === roomConfig.id
							)
							return (
								<RoomContextProvider
									key={'room' + roomConfig.id}
									roomNumber={index + 1}
									roomConfig={roomConfig}
									selectedRoom={isRoomSelected?.room}
									showTouristTax={showTouristTax}
								>
									<RoomStateSwitcher />
								</RoomContextProvider>
							)
						})}
					</Accordion.Root>
				) : (
					<Accordion.Root
						type={'single'}
						defaultValue={roomConfigurations[0].id}
						collapsible={false}
					>
						{roomConfigurations.map((roomConfig, index) => {
							const isRoomSelected = selectedRooms.find(
								(sr) => sr.roomConfigId === roomConfig.id
							)
							return (
								<RoomContextProvider
									key={'room' + roomConfig.id}
									roomNumber={index + 1}
									roomConfig={roomConfig}
									selectedRoom={isRoomSelected?.room}
									showTouristTax={showTouristTax}
								>
									<RoomStateSwitcher isSingleItem />
								</RoomContextProvider>
							)
						})}
					</Accordion.Root>
				)}
				{isStepDone && (
					<Button
						id="BookingCompletionButton"
						style="naturel"
						onClick={handleReserveOrder}
						className={cn(
							'bg-roomSelection-proceedButton text-roomSelection-proceedButtonText md:ml-[auto] md:w-1/4'
						)}
					>
						{isPending ? <Spinner /> : translate('room-selection-submit')}
					</Button>
				)}
			</div>
		</div>
	)
}
